import { MDBBadge, MDBBtn, MDBIcon, MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { formatPrenom } from "../../utils/formatPrenom";
import CustomPDFDisplay from "../assets/CustomPDFDisplay";
import { useDarkMode } from "../assets/DarkModeContext";
import PDFViewer from "../assets/PDFViewer";
import { downloadUserPdf, getUserPdfUrl } from "../services/httpService";


export default function OriginalResumePdfPreview( { currentUser, user, users, children } ) {
    const [ pdfFile, setPdfFile ] = useState( null );
    const [ isPopupVisible, setPopupVisible ] = useState( false );
    const [ uploadedFileName, setUploadedFileName ] = useState( user?.docUrl || null );
    const { isDarkMode } = useDarkMode();

    useEffect( () => {
        setUploadedFileName( user?.docUrl || null );
    }, [ user?.id, user?.docUrl ] );

    useEffect( () => {
        if ( !user?.id ) {
            return undefined;
        }

        let isCancelled = false;
        getUserPdfUrl( user.id )
            .then( async response => {
                if ( response.ok ) {
                    const fileName = await response.text();
                    if ( !isCancelled ) {
                        setUploadedFileName( fileName );
                    }
                } else if ( response.status === 404 && !isCancelled ) {
                    setUploadedFileName( null );
                }
            } )
            .catch( error => console.error( "Erreur lors du chargement du CV d'origine :", error ) );

        return () => {
            isCancelled = true;
        };
    }, [ user?.id ] );

    const displayedUsers = useMemo( () => {
        if ( users?.length ) {
            return users.map( displayedUser =>
                displayedUser.id === user?.id
                    ? { ...displayedUser, docUrl: uploadedFileName || displayedUser.docUrl }
                    : displayedUser
            );
        }

        return user?.id
            ? [ { ...user, docUrl: uploadedFileName || user.docUrl } ]
            : [];
    }, [ uploadedFileName, user, users ] );

    function closeUploadPopup() {
        setPopupVisible( false );
        setPdfFile( null );
    }

    function handleUploadSuccess( fileName ) {
        setUploadedFileName( fileName );
        closeUploadPopup();
    }

    function handleDownload( userId, docUrl ) {
        if ( userId && docUrl ) {
            downloadUserPdf( userId )
                .then( response => {
                    if ( response.ok ) {
                        return response.blob();
                    } else {
                        console.error( `Error: Server responded with status: ${ response.status }` );
                        throw new Error( `Error: ${ response.statusText }` );
                    }
                } )
                .then( blob => {
                    const pdfBlob = new Blob( [ blob ], { type: 'application/pdf' } );
                    const blobUrl = window.URL.createObjectURL( pdfBlob );
                    const link = document.createElement( 'a' );
                    link.href = blobUrl;
                    link.download = docUrl.split( /[\\/]/ ).pop();
                    document.body.appendChild( link );
                    link.click();
                    document.body.removeChild( link );
                    window.setTimeout( () => window.URL.revokeObjectURL( blobUrl ), 1000 );
                } )
                .catch( error => {
                    console.error( 'Error:', error );
                    toast.error( "Impossible de télécharger le CV d'origine." );
                } );
        } else {
            toast.error( "Aucun CV n'a été déposé." );
        }
    }


    return (
        <div>
            <div className="d-flex justify-content-between mb-3" style={ { cursor: "pointer" } }>
                <div></div>
                <div className="d-flex align-items-center">
                    <MDBBadge className="mx-2" color="info" light onClick={ () => {
                        setPopupVisible( true );
                    } }>
                        Déposer PDF
                    </MDBBadge>
                    <MDBIcon
                        fas
                        icon="plus-circle"
                        size="2x"
                        style={ { color: "#09B4BF" } }
                        onClick={ () => setPopupVisible( true ) }
                    />
                </div>
            </div>
            <div className="">
                { isPopupVisible && (
                    <div className="popup">
                        <div className="popup-content text-center">
                            <MDBBtn onClick={ closeUploadPopup }>x</MDBBtn>
                            { ( currentUser.role === "MANAGER" || currentUser.role === "SUPERADMIN" ) ? (
                                <div className="m-auto w-50">
                                    { children }
                                    <PDFViewer
                                        setPdfFile={ setPdfFile }
                                        filteredUser={ user }
                                        onUploadSuccess={ handleUploadSuccess }
                                    />
                                    <div>
                                        <CustomPDFDisplay
                                            pdfFile={ pdfFile }
                                            displayUser={ user }
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center">
                                    <PDFViewer
                                        setPdfFile={ setPdfFile }
                                        filteredUser={ currentUser }
                                        onUploadSuccess={ handleUploadSuccess }
                                    />
                                    <div>
                                        <CustomPDFDisplay
                                            pdfFile={ pdfFile }
                                            displayUser={ currentUser }
                                        />
                                    </div>
                                </div>
                            ) }
                        </div>
                    </div>
                ) }
            </div>
            <div
                className="shadow-4 rounded-5 table-container"
                style={ { overflowX: "auto" } }
            >
                <MDBTable>
                    <MDBTableHead className={ `${ isDarkMode ? "bg-dark-mode" : "" }` }>
                        <tr style={ { color: "#09B4BF" } }>
                            <th>Nom et prénom</th>
                            <th className="text-center">Nom du fichier</th>
                            <th className="text-center">Fichier</th>
                        </tr>
                    </MDBTableHead>
                    <MDBTableBody style={ { verticalAlign: "middle" } }>
                        { displayedUsers.map( ( user ) => (
                            <tr key={ user.id }
                                className={ isDarkMode ? "text-white" : "text-muted" }>
                                <td>
                                    <div className="d-flex align-items-center">
                                        <img
                                            src="./spinner-alcyone.png"
                                            alt=""
                                            style={ { width: "45px", height: "45px" } }
                                            className="rounded-circle"
                                        />
                                        <p className="fw-bold mb-1">
                                            <span className="text-uppercase">{ user.nom }</span>{ " " }
                                            { formatPrenom( user.prenom ) }
                                        </p>
                                    </div>
                                </td>
                                <td className="text-center">
                                    { user.docUrl ? (
                                        user.docUrl.split( /[\\/]/ ).pop()
                                    ) : (
                                        "Aucun fichier"
                                    ) }
                                </td>
                                <td className="text-center">
                                    <p className="mb-0">
                                        { user.docUrl ? (
                                            <MDBIcon
                                                far icon="file-pdf"
                                                size="2x"
                                                style={ { color: "#09B4BF", cursor: "pointer" } }
                                                onClick={ () => handleDownload( user.id, user.docUrl ) }
                                                title="Télécharger le PDF"
                                            />
                                        ) : (
                                            "Aucun CV n'a été déposé."
                                        ) }
                                    </p>
                                </td>
                            </tr>
                        ) ) }
                    </MDBTableBody>
                </MDBTable>
            </div>
        </div>
    );
}

OriginalResumePdfPreview.propTypes = {
    currentUser: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
    users: PropTypes.arrayOf( PropTypes.object ),
    children: PropTypes.node,
};
